import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { GetMaterialFavoritesDocument, type GetMaterialFavoritesQuery } from "@/graphql";
import { getMaterialsByFilterAndSearchV2, type MaterialSearchPayload } from "@/api/services/Materials";
import type { Material } from "@/types/Views/MaterialDatabase/Material";
import gaiupApolloClient from "@/graphql/services/ApolloClient";

export const useLibrary = defineStore("library", () => {
  // State
  const selectedMaterial = ref<"construction" | "material">("material");
  const materialCategories = ref<Record<"construction" | "material", Material[]>>({
    material: [],
    construction: [],
  });
  const favoriteMaterialIds = ref<string[]>([]);
  const favoritesLoaded = ref(false);

  // Write-only: the last library item picked (via setLibrary). Never read anywhere yet, so typed
  // unknown rather than a specific shape — the two callers pass slightly different objects.
  const selectedLibrary = ref<unknown>(null);

  const PAGE_SIZE = 10;

  // Setters
  const setMaterialType = (name: "construction" | "material") => {
    selectedMaterial.value = name;
  };

  const setLibrary = (library: unknown) => {
    selectedLibrary.value = library;
  };

  // Actions
  // `scope` carries the caller's own narrowing for the unfiltered listing — the Library page's
  // Constructions tab uses it to keep inhomogeneous layers out of the catalogue (RTLCA-5571, see
  // Library.vue). Spread first, so the tab and paging arguments this store owns always win.
  const fetchLibraryMaterials = async (
    reset = false,
    page = 1,
    minValidTo?: string,
    scope?: Pick<MaterialSearchPayload, "isCustom">
  ) => {
    const data = await getMaterialsByFilterAndSearchV2({
      ...scope,
      isConstruction: isConstruction.value,
      pageSize: PAGE_SIZE,
      pageNumber: page,
      ...(minValidTo ? { minValidTo } : {}),
    });

    if (reset) {
      materialCategories.value[selectedMaterial.value] = data;
    } else {
      materialCategories.value[selectedMaterial.value].push(...data);
    }

    return data;
  };

  const searchLibraryMaterials = async (payload: MaterialSearchPayload, append = false) => {
    // collections
    const data = await getMaterialsByFilterAndSearchV2({
      pageSize: PAGE_SIZE,
      ...payload,
      isConstruction: isConstruction.value,
    });

    if (append) {
      materialCategories.value[selectedMaterial.value].push(...data);
    } else {
      materialCategories.value[selectedMaterial.value] = data;
    }

    return data;
  };

  // Drops the list for the active tab. The search actions assign only after their request resolves,
  // so a failed search leaves the previous query's results in place; the caller uses this to clear
  // them rather than presenting them as the answer to a query that never completed (RTLCA-5380).
  const clearMaterials = () => {
    materialCategories.value[selectedMaterial.value] = [];
  };

  const removeMaterial = (materialId: string, isConstruction: boolean) => {
    const type = isConstruction ? "construction" : "material";
    materialCategories.value[type] = materialCategories.value[type].filter((m) => m.id !== materialId);
  };

  const fetchFavoriteMaterialIds = async (force = false) => {
    if (!force && favoritesLoaded.value) return;
    const { data } = await gaiupApolloClient.query<GetMaterialFavoritesQuery>({
      query: GetMaterialFavoritesDocument,
      fetchPolicy: "network-only",
    });

    favoriteMaterialIds.value = data.userMaterialFavorite.map((material) => material.materialInternalId);
    favoritesLoaded.value = true;
  };

  // Getters
  const libraryMaterial = computed(() => {
    return materialCategories.value[selectedMaterial.value] || [];
  });

  const isConstruction = computed(() => {
    return selectedMaterial.value === "construction";
  });

  return {
    // state
    selectedMaterial,
    materialCategories,
    favoriteMaterialIds,
    favoritesLoaded,
    selectedLibrary,

    // setters
    setMaterialType,
    setLibrary,

    // actions
    fetchLibraryMaterials,
    searchLibraryMaterials,
    clearMaterials,
    removeMaterial,
    fetchFavoriteMaterialIds,

    // getters
    libraryMaterial,
    isConstruction,
  };
});
